import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, DataTable, Field, LoadMore, Pill, ProblemNotice, type Column } from '../../components/ui'
import { type AuditFilters, type AuditRecord, listAuditEvents } from '../../lib/api'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { formatDateTime } from '../../lib/format'

/**
 * The audit log (§5.5) — filterable by actor, action and target; paginated;
 * read-only.
 *
 * Read-only is not a promise this screen makes. `audit_events` refuses UPDATE,
 * DELETE and TRUNCATE at the database, so there is no write path to omit.
 *
 * `outcome` is the column worth having and the one an operator will not think
 * to ask for: Cognito and PostgreSQL cannot commit together, so every lifecycle
 * operation has a side that can succeed while the other fails. "The admin
 * clicked suspend" is not the question an incident asks; "did they actually
 * lose access" is.
 */
export function Audit() {
  const [rows, setRows] = useState<AuditRecord[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [filters, setFilters] = useState<AuditFilters>({})
  const [draft, setDraft] = useState({ actor_user_id: '', action: '', target_id: '' })
  const [isLoading, setLoading] = useState(true)
  const [isPaging, setPaging] = useState(false)
  // See `Users.tsx` for why this is a shape rather than a string: `err.message`
  // kept the server's detail and dropped its code, and there was no retry on
  // any admin screen at all.
  /**
   * A retry is named, NOT captured.
   *
   * It was `retry?: () => void`, built at the moment of failure — so the page
   * retry held the cursor from the render that failed. Measured: two clicks
   * after a failed `loadMore` issued four requests with cursors
   * [null, CUR, CUR, CUR], walking straight past `if (!cursor) return` because
   * the closure's `cursor` was stale, and re-appending a page that was already
   * on screen. Naming the retry and resolving it at RENDER time means it always
   * runs against current state.
   */
  const [error, setError] = useState<{ text: string; cause: unknown; retry?: 'list' | 'page' } | null>(null)
  // The notice above unmounts when this clears; see `useRetryLanding`.
  const [landOn, armLanding] = useRetryLanding(error !== null)
  const [reloadToken, setReloadToken] = useState(0)
  const retryList = useCallback(() => {
    setLoading(true)
    setReloadToken((n) => n + 1)
  }, [])
  const requestId = useRef(0)

  useEffect(() => {
    const mine = ++requestId.current
    let cancelled = false
    void (async () => {
      try {
        const page = await listAuditEvents(filters)
        if (cancelled || mine !== requestId.current) return
        setRows(page.items)
        setCursor(page.next_cursor ?? null)
        setError(null)
      } catch (err) {
        if (!cancelled && mine === requestId.current) {
          setError({ text: 'The audit log could not be loaded.', cause: err, retry: 'list' })
        }
      } finally {
        if (!cancelled && mine === requestId.current) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [filters, reloadToken, retryList])

  const loadMore = async () => {
    if (!cursor) return
    setPaging(true)
    try {
      const page = await listAuditEvents({ ...filters, cursor })
      setRows((prev) => [...prev, ...page.items])
      setCursor(page.next_cursor ?? null)
      setError(null)
    } catch (err) {
      setError({ text: 'The next page could not be loaded.', cause: err, retry: 'page' })
    } finally {
      setPaging(false)
    }
  }

  const apply = () => {
    setLoading(true)
    setFilters({
      actor_user_id: draft.actor_user_id.trim() || undefined,
      action: draft.action.trim() || undefined,
      target_id: draft.target_id.trim() || undefined,
    })
  }

  const columns: Column<AuditRecord>[] = [
    {
      key: 'when',
      header: 'When',
      render: (r) => <span className="mono-cell">{formatDateTime(r.created_at)}</span>,
    },
    { key: 'action', header: 'Action', render: (r) => <span className="mono-cell">{r.action}</span> },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (r) => (
        <Pill tone={r.outcome === 'succeeded' ? 'green' : r.outcome === 'partial' ? 'amber' : 'red'} dot>
          {r.outcome}
        </Pill>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (r) => <span className="mono-cell">{r.actor_user_id ?? r.actor_source ?? 'system'}</span>,
    },
    {
      key: 'target',
      header: 'Target',
      render: (r) => (
        <span className="mono-cell">
          {r.target_type ? `${r.target_type} ` : ''}{r.target_id ?? '—'}
        </span>
      ),
    },
  ]

  return (
    <div className="admin-page stack stack--wide">
      <header className="page">
        <h1>Audit log</h1>
        <p className="page__lead">
          Every administrative change, newest first. Append-only at the database — there
          is no way to edit or remove an entry, from here or anywhere else.
        </p>
      </header>

      {/* A pending list commit can restore controlled input props before the
          native change reaches React in WebKit. Accept edits after that read
          settles, so the entered filter cannot be erased by its response. */}
      <div className="toolbar">
        <div className="toolbar__grow">
          <Field label="Actor user id" value={draft.actor_user_id} disabled={isLoading}
            onChange={(e) => setDraft((d) => ({ ...d, actor_user_id: e.target.value }))} />
        </div>
        <div className="toolbar__grow">
          {/*
            `user.approved`, NOT `account.approved`. The vocabulary is
            `brainspot/application/audit.py:41-63` and every constant in it is
            prefixed `user.` or `auth.` -- there is no `account.` action and
            never has been. The filter is an EXACT match at the database
            (`storage/postgres.py:1124`), so an operator who typed the value
            this placeholder suggested got "Nothing has been recorded for these
            filters" and a reasonable conclusion that nothing had happened.

            The same wrong prefix was in the MSW fixtures and in the live spec,
            where it was found and corrected on 2026-09-12. Both of those agreed
            with each other and with nothing else. This one was missed in the
            same sweep, and it is the only one a user could ever see.
          */}
          <Field label="Action" value={draft.action} placeholder="user.approved" disabled={isLoading}
            onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))} />
        </div>
        <div className="toolbar__grow">
          <Field label="Target id" value={draft.target_id} disabled={isLoading}
            onChange={(e) => setDraft((d) => ({ ...d, target_id: e.target.value }))} />
        </div>
        <Button variant="secondary" onClick={apply} busy={isLoading}>Filter</Button>
      </div>

      {error && (
        <ProblemNotice
          error={error.cause}
          onRetry={error.retry === 'list' ? retryList : error.retry === 'page' ? () => void loadMore() : undefined}
          retryBusy={error.retry === 'page' && isPaging}
          landing={armLanding}
        >
          {error.text}
        </ProblemNotice>
      )}

      {/* WHERE FOCUS GOES WHEN THE RETRY ABOVE WORKS.
          The notice unmounts when the error clears, taking the focused "Try
          again" with it -- `Button`'s own restore cannot help, because there is
          nothing left to restore to. The list is what now says what happened,
          so a keyboard user lands on the answer rather than at the top of the
          document. `tabIndex={-1}` keeps it out of the tab order; the global
          `:focus-visible` ring (base.css:62) is the only styling it needs. */}
      <div className="retry-landing" ref={landOn} tabIndex={-1}>
        <DataTable
          caption="Audit events"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          empty={
            <>
              <strong>No matching events.</strong>
              <span>Nothing has been recorded for these filters.</span>
            </>
          }
        />

        {!!rows.length && (
          <LoadMore loaded={rows.length} nextCursor={cursor} isLoading={isPaging}
            noun="event" onLoadMore={() => void loadMore()} />
        )}
      </div>
    </div>
  )
}
